import { useApolloClient } from '@apollo/react-hooks';
import { graphqlApiSite } from 'core/api/graphql';
import { showSnackbar } from 'core/components/snackbar';
import useToggle from 'core/hooks/useToggle';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import React, { useEffect, useRef } from 'react';
import { EventRecord } from 'site/graphql';
import MenuIcon from '../../../../svg/vertical-three-dots.svg';
import { default as ignoreCodeGenGql } from 'graphql-tag';

export const SET_VIEWED = ignoreCodeGenGql`
    query SetViewed($id: Int!) {
        setViewed(id: $id) {
            id
            isViewed
        }
    }
`;

type IItemCardMenuProps = WithStyles<typeof styles> & {
    eventRecord: Pick<
        EventRecord,
        'id' | 'description' | 'title' | 'date' | 'hours' | 'isViewed' | 'link' | 'pictureId'
    >;
    productFormType: string;
    typename: string;
};

const ItemCardMenu: React.FC<IItemCardMenuProps> = ({ classes, eventRecord, productFormType, typename }) => {
    const [toggle, { offToggle, switchToggle }] = useToggle();
    const { id, isViewed } = eventRecord;
    const client = useApolloClient();
    const ref = useRef(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();

        // помечаем форм итем просмотренным/непросмотренным
        const query = `mutation {
            markFormItemAsViewedOrUnviewed(productFormType: ${productFormType}, formItemId: ${id})
        }`;
        graphqlApiSite<{ id: number }>(query, null)
            .then(() => {
                client.writeQuery({
                    query: SET_VIEWED,
                    variables: { id },
                    data: {
                        setViewed: { id, isViewed: !isViewed, __typename: typename },
                    },
                });
            })
            .then(offToggle)
            .catch(error => {
                showSnackbar({ snackbarErrors: error });
            });
    };

    // Click outside changes toggle state
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                offToggle();
            }
        }

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={ref} className={classes.container}>
            <MenuIcon className={classes.menuIcon} onClick={switchToggle} />
            {toggle && (
                <div className={classes.mark}>
                    <div className={classes.menuItem} onClick={handleClick}>
                        Пометить как {isViewed ? 'непросмотренное' : 'просмотренное'}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            width: 20,
        },
        mark: {
            background: 'white',
            border: `1px solid ${theme.colors.lightGray}`,
            width: 290,
            zIndex: 2,
            position: 'absolute',
            right: -290,
            color: theme.colors.textMedium,

            [theme.breakpoints.down('sm')]: {
                width: 150,
                right: 20,
            },
        },
        menuIcon: {
            width: 20,
            height: 20,
            fill: '#A7A7A7',
            position: 'absolute',
            cursor: 'pointer',

            '&:hover': {
                fill: '#777777',
            },
        },
        menuItem: {
            padding: '5px 10px',
            cursor: 'pointer',

            '&:hover': {
                backgroundColor: theme.colors.lightGray,
            },
        },
    });

export default injectSheet(styles)(ItemCardMenu);
